import "@/App.css";
import "./i18n";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { Toaster } from "./components/ui/sonner";
import ErrorBoundary from "./components/ErrorBoundary";
import AuthPage from "./pages/AuthPage";
import Dashboard from "./pages/Dashboard";
import NewArticle from "./pages/NewArticle";
import ArticleDetail from "./pages/ArticleDetail";
import AdminPanel from "./pages/AdminPanel";
import TermsPage from "./pages/TermsPage";
import Pricing from "./pages/Pricing";
import PaymentSuccess from "./pages/PaymentSuccess";
import UserSettings from "./pages/UserSettings";
import PublicationCalendar from "./pages/PublicationCalendar";
import Resources from "./pages/Resources";
import ArmeriaIndex from "./pages/armeria/ArmeriaIndex";
import ArmeriaToolPage from "./pages/armeria/ArmeriaToolPage";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen" style={{ background: "#0B1622" }} />;
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen" style={{ background: "#0B1622" }} />;
  if (user) {
    // If user came from La Armería, send them back so the tool auto-resumes.
    // Check URL param `next` first (more resilient), then localStorage.
    try {
      const urlNext = new URLSearchParams(window.location.search).get("next");
      if (urlNext && urlNext.startsWith("/armeria/")) {
        return <Navigate to={urlNext} replace />;
      }
      const stored = localStorage.getItem("armeria_return_to");
      if (stored && stored.startsWith("/armeria/")) {
        localStorage.removeItem("armeria_return_to");
        return <Navigate to={stored} replace />;
      }
    } catch { /* silent */ }
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen" style={{ background: "#0B1622" }} />;
  if (!user) return <Navigate to="/" replace />;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<PublicRoute><AuthPage /></PublicRoute>} />
            <Route path="/registro" element={<PublicRoute><AuthPage /></PublicRoute>} />
            <Route path="/signup" element={<PublicRoute><AuthPage /></PublicRoute>} />
            <Route path="/login" element={<PublicRoute><AuthPage /></PublicRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><ErrorBoundary><Dashboard /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/new" element={<ProtectedRoute><ErrorBoundary><NewArticle /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/articles/:id" element={<ProtectedRoute><ErrorBoundary><ArticleDetail /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/pricing" element={<ProtectedRoute><ErrorBoundary><Pricing /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><ErrorBoundary><UserSettings /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/calendar" element={<ProtectedRoute><ErrorBoundary><PublicationCalendar /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/resources" element={<ProtectedRoute><ErrorBoundary><Resources /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/payment-success" element={<ProtectedRoute><ErrorBoundary><PaymentSuccess /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/admin" element={<AdminRoute><ErrorBoundary><AdminPanel /></ErrorBoundary></AdminRoute>} />
            <Route path="/terms" element={<ErrorBoundary><TermsPage /></ErrorBoundary>} />
            {/* La Armería — public tools (login gate on generate) */}
            <Route path="/armeria" element={<ErrorBoundary><ArmeriaIndex /></ErrorBoundary>} />
            <Route path="/armeria/:toolId" element={<ErrorBoundary><ArmeriaToolPage /></ErrorBoundary>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="bottom-right" theme="dark" />
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
